import { useState } from 'react';

import { DBCategory } from '@core/types/types.db';
import { CategoryDeleteEndpoint } from '@core/types/types.endpoint.category';
import { DirectoryRefreshEndpoint } from '@core/types/types.endpoint.directory';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@frontend/components/ui/alert-dialog';
import { Label } from '@frontend/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@frontend/components/ui/select';
import { useDirectoryUpdater } from '@frontend/shared/hooks/shared.hook.useDirectoryUpdater';
import { useDirectoryCategories } from '@frontend/shared/hooks/useDirectory';
import { useSubmitter } from '@frontend/shared/hooks/useSubmitter';
import { callApi } from '@frontend/shared/util/util.callApi';

interface CategoryDeleteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  category: DBCategory;
  isDefault?: boolean;
}

export function CategoryDeleteDialog({
  isOpen,
  onClose,
  onSuccess,
  category,
  isDefault = false,
}: CategoryDeleteDialogProps) {
  const updateDirectory = useDirectoryUpdater();
  const categories = useDirectoryCategories();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');

  const submitter = useSubmitter(async () => {
    const [error] = await callApi<CategoryDeleteEndpoint>(
      '/categories/delete',
      {
        id: category.id,
        moveQuestionsToCategoryId: selectedCategoryId,
      },
    );

    if (error) {
      // Handle error - could show toast or set error state
      console.error('Failed to delete category:', error.error);
      return;
    }

    // Refresh categories in directory
    const [, refreshRes] = await callApi<DirectoryRefreshEndpoint>(
      '/directory/refresh',
      { categories: true },
    );
    if (refreshRes) {
      updateDirectory(refreshRes);
    }

    onSuccess();
  });

  const handleClose = () => {
    if (!submitter.isSubmitting) {
      onClose();
    }
  };

  // Get categories available for moving questions (excluding the current one)
  const availableCategories = categories.filter((c) => c.id !== category.id);

  // For now, assume category has questions if we have more than one category
  // TODO: Replace with actual question count from category object
  const hasQuestions = availableCategories.length > 0;

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Category</AlertDialogTitle>
        </AlertDialogHeader>

        <AlertDialogDescription asChild>
          <div className="flex flex-col gap-3">
            {isDefault ? (
              <>
                <p className="font-semibold text-red-600">
                  Cannot delete the default category
                </p>
                <p className="text-sm">
                  The category <strong>&ldquo;{category.name}&rdquo;</strong> is
                  currently set as the default category and cannot be deleted.
                </p>
                <p className="text-sm text-gray-600">
                  To delete this category, first create another category that
                  will become the new default.
                </p>
              </>
            ) : (
              <>
                <p>
                  Are you sure you want to delete the category{' '}
                  <strong>&ldquo;{category.name}&rdquo;</strong>?
                </p>

                {hasQuestions ? (
                  <div className="flex w-full flex-col gap-3">
                    <p className="text-sm text-gray-600">
                      If this category contains questions, they will need to be
                      moved to another category.
                    </p>

                    <div className="flex flex-col gap-2">
                      <Label className="text-sm">Move questions to:</Label>
                      <Select
                        value={selectedCategoryId}
                        onValueChange={setSelectedCategoryId}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a category for existing questions" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableCategories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">
                    This action cannot be undone.
                  </p>
                )}
              </>
            )}
          </div>
        </AlertDialogDescription>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleClose}>
            {isDefault ? 'Close' : 'Cancel'}
          </AlertDialogCancel>
          {!isDefault && (
            <AlertDialogAction
              onClick={submitter.submit}
              disabled={
                (hasQuestions && !selectedCategoryId) || submitter.isSubmitting
              }
              className="bg-red-600 hover:bg-red-700"
            >
              {submitter.isSubmitting ? 'Deleting...' : 'Delete Category'}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
